import { Component, inject, signal, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, tap, finalize, switchMap } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FatService, FatFieldDto, FatFieldPhotoDto } from '../../../../../core/services/fat.service';
import { ConfirmDialogComponent } from '../../../../../shared/components/confirm-dialog.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TaskNotificationService } from '../../../../../core/services/task-notification.service';

export interface FatPhotosDialogData {
  projectId: number;
  field: FatFieldDto;
  readonly?: boolean;
}

@Component({
  selector: 'app-fat-photos-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    ConfirmDialogComponent,
    MatProgressBarModule
  ],
  templateUrl: './fat-photos-dialog.component.html',
  styleUrl: './fat-photos-dialog.component.scss'
})
export class FatPhotosDialogComponent implements OnDestroy {
  private fatService = inject(FatService);
  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  private data: FatPhotosDialogData = inject(MAT_DIALOG_DATA);
  private taskNotificationService = inject(TaskNotificationService);

  field = signal<FatFieldDto>(this.data.field);
  projectId = this.data.projectId;
  isReadonly = signal(this.data.readonly);

  showDeleteConfirm = signal(false);
  photoPendingDelete = signal<FatFieldPhotoDto | null>(null);

  // Map pour stocker les blob URLs (utilisation de signal pour déclencher le change detection)
  photoBlobUrls = signal<Map<number, SafeUrl>>(new Map());
  private photoRawUrls = new Map<number, string>();

  isDragging = signal(false);
  isUploading = signal(false);
  isMobile = signal(false);

  constructor(public dialogRef: MatDialogRef<FatPhotosDialogComponent>) {
    this.isMobile.set(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    this.loadPhotoBlobs();
  }

  ngOnDestroy(): void {
    this.photoRawUrls.forEach(url => URL.revokeObjectURL(url));
    this.photoRawUrls.clear();
    this.photoBlobUrls().clear();
  }

  private loadPhotoBlobs(): void {
    const photos = this.field().photos || [];
    photos.forEach(p => this.loadPhotoBlob(p.id));
  }

  private loadPhotoBlob(photoId: number): void {
    if (this.photoRawUrls.has(photoId)) return;

    this.fatService.getPhotoPreview(this.projectId, photoId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
        this.photoRawUrls.set(photoId, url);

        const newMap = new Map(this.photoBlobUrls());
        newMap.set(photoId, safeUrl);
        this.photoBlobUrls.set(newMap);
        this.cdr.detectChanges();
      },
      error: (err) => console.error(`Erreur chargement photo ${photoId}:`, err)
    });
  }

  getPhotoBlobUrl(photoId: number): SafeUrl | undefined {
    return this.photoBlobUrls().get(photoId);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (this.isReadonly()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  onFilesSelected(event: Event): void {
    if (this.isReadonly()) return;
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.handleFiles(Array.from(input.files));
    input.value = '';
  }

  private readonly BLOCKED_TYPES = ['image/webp'];

  private handleFiles(files: File[]): void {
    const images = files.filter(f => f.type.startsWith('image/') && !this.BLOCKED_TYPES.includes(f.type));
    if (images.length === 0) return;

    this.isUploading.set(true);
    const taskId = images.length > 1
      ? this.taskNotificationService.start('Photos FAT', `Upload de ${images.length} images en cours...`)
      : this.taskNotificationService.start('Photos FAT', 'Upload de l\'image en cours...');
    const uploads = images.map(file =>
      this.fatService.uploadPhotoAsync(this.projectId, this.field().id, file).pipe(
        switchMap(job => this.fatService.waitForPhotoUpload(this.projectId, job.jobId)),
        tap(photo => {
          const current = this.field();
          if (!current.photos) current.photos = [];
          current.photos.push(photo);
          this.field.set({ ...current });
          this.loadPhotoBlob(photo.id);
        }),
        catchError(() => of(null))
      )
    );

    forkJoin(uploads).pipe(finalize(() => this.isUploading.set(false))).subscribe({
      next: (results) => {
        const uploadedCount = results.filter(Boolean).length;
        if (uploadedCount === images.length) {
          this.taskNotificationService.succeed(taskId, images.length > 1 ? `${uploadedCount} images uploadées.` : 'Image uploadée.');
        } else {
          this.taskNotificationService.fail(taskId, 'Une ou plusieurs images n\'ont pas pu être uploadées.');
        }
      },
      error: () => this.taskNotificationService.fail(taskId, 'L\'upload des images a échoué.')
    });
  }

  askDeletePhoto(photo: FatFieldPhotoDto): void {
    this.photoPendingDelete.set(photo);
    this.showDeleteConfirm.set(true);
  }

  confirmDeletePhoto(): void {
    const photo = this.photoPendingDelete();
    if (!photo) return;

    this.fatService.deletePhoto(this.projectId, photo.id).subscribe({
      next: () => {
        const current = this.field();
        current.photos = (current.photos || []).filter(p => p.id !== photo.id);
        this.field.set({ ...current });

        const raw = this.photoRawUrls.get(photo.id);
        if (raw) URL.revokeObjectURL(raw);
        this.photoRawUrls.delete(photo.id);

        const newMap = new Map(this.photoBlobUrls());
        newMap.delete(photo.id);
        this.photoBlobUrls.set(newMap);
        this.cancelDeletePhoto();
      },
      error: () => this.cancelDeletePhoto()
    });
  }

  cancelDeletePhoto(): void {
    this.showDeleteConfirm.set(false);
    this.photoPendingDelete.set(null);
  }

  downloadPhoto(photo: FatFieldPhotoDto): void {
    this.fatService.getPhotoDownload(this.projectId, photo.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = photo.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      error: (err) => console.error('Erreur téléch photo:', err)
    });
  }

  close(): void {
    this.dialogRef.close(this.field());
  }
}
